import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';

export interface ProductLineElement {
    'productLineID': number,
    'name': string,
    'icon': number
}


@Injectable()
export class ProductLineService {
    productLineList: BehaviorSubject<ProductLineElement[] | null> = new BehaviorSubject<ProductLineElement[] | null>(null);
    productLineKey: BehaviorSubject<{ [key: string]: string } | null> = new BehaviorSubject<{ [key: string]: string } | null>(null);
    loadFailed = false;

    constructor(private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService) { };

    get productLineListUpdated(): Observable<ProductLineElement[] | null> {
        return this.productLineList.asObservable();
    }

    get productLineKeyUpdated(): Observable<{ [key: string]: string } | null> {
        return this.productLineKey.asObservable();
    }

    createKey(productLineList: ProductLineElement[]) {
        let keyObj: { [key: string]: string } = {};

        productLineList.forEach(element => {
            keyObj[element.productLineID.toString()] = element.name;
        });

        return keyObj
    }

    refresh() {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        this.http.get<any>(environment.azure.url + '/productlines', options)
            .subscribe({
                next: data => {
                    this.productLineList.next(data);
                    this.productLineKey.next(this.createKey(data));
                    this.loadFailed = false;
                },
                error: msg => {
                    this.loadFailed = true;
                    this.productLineList.next(null);
                    console.log(msg);
                }
            });
    }

    add(target: ProductLineElement) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/admin.productlines.add',
            {
                name: target.name,
                icon: target.icon
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'SLA Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add SLA. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    update(target: ProductLineElement) {
        // Update target in databases
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/admin.productlines.update',
            {
                productLineID: target.productLineID,
                name: target.name,
                icon: target.icon
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'SLA Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update product lines. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    remove(target: ProductLineElement) {
        //Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/admin.productlines.remove',
            {
                productLineID: target.productLineID
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'SLA Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove product line. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }
}